/**
 * SubscriptionBookingModal Component
 *
 * Modal for confirming subscription booking via Stripe Checkout.
 * Shows selected configuration and redirects to Stripe Checkout.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState } from 'react';
import { X, CreditCard, Info, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/v2/ui/Button';
import { Alert, AlertDescription } from '@/components/v2/ui';
import { createCheckoutSession } from '@/api/subscriptions';
import { toast } from 'sonner';

interface SubscriptionBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: {
    tierName: string;
    tierCode: string; // Changed from priceId to tierCode (backend creates Price lazily)
    monthlyPrice: number;
    annualPrice: number;
    credits?: number;
    products?: number;
    channels?: number;
  };
  billingCycle: 'monthly' | 'annual';
}

export function SubscriptionBookingModal({ isOpen, onClose, config, billingCycle }: SubscriptionBookingModalProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const displayPrice = billingCycle === 'annual' ? config.annualPrice : config.monthlyPrice;

  const handleCheckout = async () => {
    setIsLoading(true);

    try {
      // Read test_clock_id from URL query param (staging/dev only)
      const testClockId = new URLSearchParams(window.location.search).get('test_clock') ?? undefined;

      const requestData = {
        tier_code: config.tierCode,
        billing_cycle: billingCycle,
        pricing_model: (config.tierCode === 'starter' ? 'credit' : 'dimension') as 'credit' | 'dimension',
        credits: config.credits,
        products: config.products,
        channels: config.channels,
        test_clock_id: testClockId,
      };

      // Create checkout session (handles both new + upgrade)
      // For upgrades: Old subscription cancelled, remaining credits converted to purchased credits
      const result = await createCheckoutSession(requestData);

      if (!result || !result.success) {
        toast.error(result?.error || t('errors.saving'));
        setIsLoading(false);
        return;
      }

      if (result.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = result.checkout_url;
        return;
      }

      if (result.change_type === 'upgrade') {
        // In-place upgrade completed (no checkout needed)
        toast.success(result.message || t('billing.upgradeSuccess'));
        onClose();
        setTimeout(() => window.location.reload(), 1500);
        return;
      }

      if (result.change_type === 'downgrade') {
        // Downgrade scheduled for period end
        toast.info(result.message || t('billing.downgradePlanned'));
        onClose();
        setTimeout(() => window.location.reload(), 1500);
        return;
      }

      toast.error(t('errors.unexpected'));
      setIsLoading(false);
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(t('errors.unexpected'));
      setIsLoading(false);
    }
  };

  // Normal booking modal
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full"
        role="dialog"
        aria-labelledby="modal-title"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <h2 id="modal-title" className="text-xl font-bold text-gray-900 dark:text-gray-50 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary-600" />
            {t('billing.bookSubscription')}
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <ConfigurationSummary config={config} billingCycle={billingCycle} displayPrice={displayPrice} />
          <StripeNotice />
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-gray-800">
          <Button variant="outline" onClick={onClose} disabled={isLoading} className="flex-1">
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleCheckout} disabled={isLoading} className="flex-1 flex items-center justify-center gap-2">
            <CreditCard className="h-4 w-4" />
            {isLoading ? t('common.loading') : t('billing.payWithStripe')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConfigurationSummary({
  config,
  billingCycle,
  displayPrice,
}: {
  config: SubscriptionBookingModalProps['config'];
  billingCycle: 'monthly' | 'annual';
  displayPrice: number;
}) {
  const { t } = useTranslation();
  return (
    <div className="p-4 bg-primary-50 dark:bg-primary-950/20 rounded-lg border border-primary-200 dark:border-primary-900">
      <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-100 mb-3">{config.tierName}</h3>

      <div className="space-y-2 text-sm text-primary-800 dark:text-primary-200">
        {config.credits && <ConfigRow label={t('billing.creditsPerMonth')} value={config.credits.toLocaleString()} />}
        {config.products && <ConfigRow label={t('billing.products')} value={config.products.toLocaleString()} />}
        {config.channels && <ConfigRow label="Channels" value={config.channels.toString()} />}

        <div className="pt-2 mt-2 border-t border-primary-200 dark:border-primary-800">
          <div className="flex justify-between items-baseline">
            <span className="font-semibold">{t('billing.price')}</span>
            <div className="text-right">
              <span className="text-2xl font-bold text-primary-900 dark:text-primary-100">
                {displayPrice.toFixed(2).replace('.', ',')} €
              </span>
              <span className="text-sm ml-1">{billingCycle === 'annual' ? t('billing.perYear') : t('billing.perMonth')}</span>
            </div>
          </div>
          {billingCycle === 'annual' && (
            <p className="text-xs text-emerald-600 dark:text-emerald-400 text-right mt-1 font-medium">
              {t('billing.twoMonthsFree')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ConfigRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span>{label}:</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function StripeNotice() {
  const { t } = useTranslation();
  return (
    <>
      <Alert variant="info">
        <Info className="h-4 w-4" />
        <AlertDescription>
          <div className="text-sm">
            <div className="font-semibold mb-1">💳 {t('billing.securePayment')}</div>
            <div>{t('billing.stripeCheckoutInfo')}</div>
          </div>
        </AlertDescription>
      </Alert>

      <Alert variant="success">
        <Sparkles className="h-4 w-4" />
        <AlertDescription>
          <div className="text-sm">
            <div className="font-semibold mb-1">✨ {t('billing.creditConversionTitle')}</div>
            <div>{t('billing.creditConversionInfo')}</div>
          </div>
        </AlertDescription>
      </Alert>
    </>
  );
}
